import React, { ReactNode } from 'react'
import { WordState, HideWordMode, ShowWordMode } from '../types'
import { ColorUtils } from '../utils/colorUtils'

interface WordProps {
	word: WordState;
	hideWordMode: HideWordMode;
	showWordMode: ShowWordMode;
	onToggle: (wordId: string) => void;
	plain?: boolean; // New prop for plain words
}

export const Word: React.FC<WordProps> = ({
	word,
	hideWordMode,
	showWordMode,
	onToggle,
	plain = false,
}) => {
	const displayedText = word.text.replace(/_/g, ' ')

	const handleClick = () => {
		if (word.text !== '\n' && !plain) {
			onToggle(word.id)
		}
	}

	// Handle line breaks
	if (word.text === '\n') {
		return <br />
	}

	// all the ways you would need to render the word width
	const renderWord = showWordMode != 'symbol' || !word.symbol || hideWordMode == 'firstLetter' || hideWordMode == 'firstLetterEmoji'
	const renderSymbol = !!word.symbol && (showWordMode == 'both' || showWordMode == 'symbol' || showWordMode == 'firstLetterEmoji' || hideWordMode == 'symbol' || hideWordMode == 'firstLetterEmoji')

	let wordContent: ReactNode | null = null
	if (renderWord) {
		const useFirstLetters = (word.isVisible && (showWordMode == 'firstLetter' || showWordMode == 'firstLetterEmoji')) ||
								(!word.isVisible && (hideWordMode == 'firstLetter' || hideWordMode == 'firstLetterEmoji'))
		wordContent = <span className='word-content'>
			{useFirstLetters ? renderFirstLetters(displayedText) : displayedText}
		</span>
	}

	let symbolContent: ReactNode | null = null
	if (renderSymbol) {
		const symbol = word.symbol!
		if (symbol.type === 'gospel') {
			symbolContent = <img src={symbol.src} /* srcSet={symbol.srcset} sizes={symbol.sizes} */ alt={displayedText} />
		} else if (symbol.type === 'emoji') {
			symbolContent = symbol.symbol
		}
		symbolContent = <span className="symbol-content">{symbolContent}</span>
	}

	return (
		<span
			tabIndex={plain ? undefined : 0}
			className={`word ${!plain ? 'word--clickable' : ''} ${word.isVisible ? 'word--visible' : !plain ? 'word--hidden' : ''}`}
			onKeyDown={e => { if (e.key == ' ') handleClick() }}
			onClick={handleClick}
			data-word-id={word.id}
			data-original-text={word.text}
			style={getWordStyle(word, plain)}
		>
			{wordContent} {symbolContent}
		</span>
	)
}

function renderFirstLetters(text: string) {
	const parts = text.split(' ')
	return (
		<>
			{parts.map((part, index) => (
				<React.Fragment key={index}>
					<span style={{ color: 'inherit' }}>{part.charAt(0)}</span>
					<span style={{ visibility: 'hidden' }}>{part.slice(1)}</span>
					{index < parts.length - 1 && ' '}
				</React.Fragment>
			))}
		</>
	)
}

function getWordStyle(word: WordState, plain: boolean) {
	const baseColor = ColorUtils.getColorForWord(word.text.toLowerCase())

	const textColor = 'light-dark(black, white)'

	if (plain) {
		return {
			backgroundColor: 'transparent',
			borderColor: 'transparent',
			color: textColor,
		}
	}

	return {
		backgroundColor: word.isVisible
			? `light-dark(${baseColor}88, ${baseColor}99)` // Light background for visible words
			: `light-dark(${baseColor}bb, ${baseColor}cc)`, // Fuller color for hidden words
		color: textColor,
		borderColor: word.isVisible ? baseColor + 'aa' : 'transparent',
	}
}
